import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as playersApi from "@api/players.api";
import type { Player } from "@/types";

interface PlayersState {
  items: Player[];
  loading: boolean;
}

const initialState: PlayersState = {
  items: [],
  loading: false,
};

export const fetchPlayers = createAsyncThunk("players/fetchAll", async () => {
  return await playersApi.getPlayers();
});

export const createPlayer = createAsyncThunk(
  "players/create",
  async (data: {
    name: string;
    defense: number;
    agility: number;
    technique: number;
  }) => {
    return await playersApi.createPlayer(data);
  },
);

export const updatePlayer = createAsyncThunk(
  "players/update",
  async ({
    id,
    data,
  }: {
    id: string;
    data: {
      name?: string;
      defense?: number;
      agility?: number;
      technique?: number;
    };
  }) => {
    return await playersApi.updatePlayer(id, data);
  },
);

export const removePlayer = createAsyncThunk(
  "players/remove",
  async (id: string) => {
    await playersApi.deletePlayer(id);
    return id;
  },
);

export const updateBonusPoints = createAsyncThunk(
  "players/updateBonus",
  async ({ id, bonusPoints }: { id: string; bonusPoints: number }) => {
    return await playersApi.updateBonusPoints(id, bonusPoints);
  },
);

const playersSlice = createSlice({
  name: "players",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPlayers.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchPlayers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createPlayer.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updatePlayer.fulfilled, (state, action) => {
        const idx = state.items.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(removePlayer.fulfilled, (state, action) => {
        state.items = state.items.filter((p) => p.id !== action.payload);
      })
      .addCase(updateBonusPoints.fulfilled, (state, action) => {
        const idx = state.items.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      });
  },
});

export default playersSlice.reducer;
